import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import { PropertyForm, PropertyFormValues } from "../../components/property-form";
import { useGetProperty } from "../../queries/property";
import { updateProperty } from "../../services/property";
import { navigateBack } from "../../utils/navigate-back";

export function MaintenancePropertyEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Property ID is required");
  }

  const { data: property, isLoading: isLoadingProperty } = useGetProperty(id);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formValues, setFormValues] = useState<PropertyFormValues>({
    name: "",
    parts: [],
  });

  useEffect(() => {
    if (property) {
      setFormValues({
        name: property.name,
        parts: property.parts.map((part) => ({
          id: part.id,
          code: part.code,
          name: part.name,
        })),
      });
    }
  }, [property]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/maintenance/property");
  };

  return (
    <Modal open onClose={closeModal}>
      <PropertyForm
        hasError={hasError}
        loading={isLoadingProperty || loading}
        values={formValues}
        onCancel={closeModal}
        onChange={setFormValues}
        onSubmit={() => {
          void (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await updateProperty(id, {
                name: formValues.name,
                parts: formValues.parts.map((part) => ({
                  ...(part.id && { id: part.id }),
                  code: part.code,
                  name: part.name,
                })),
              });
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["property", id] }),
                queryClient.invalidateQueries({ queryKey: ["properties"] }),
                queryClient.invalidateQueries({ queryKey: ["propertyParts"] }),
                queryClient.invalidateQueries({ queryKey: ["maintenancePlans"] }),
              ]);
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
